import React, { useState, useEffect } from 'react';
import { fetchUserAnalytics, fetchAnalyticsStats, fetchUsageTrends } from '../../../services/analyticsService';
import { Clock, Users, Activity, Calendar, Monitor } from 'lucide-react';
import UserAnalyticsTable from './UserAnalyticsTable';
import UsageTrendsChart from './UsageTrendsChart';

export default function UserAnalyticsSection() {
  const [analytics, setAnalytics] = useState([]);
  const [stats, setStats] = useState({
    totalActiveUsers: 0,
    totalSessions: 0,
    totalUsageHours: 0,
    avgUsagePerUser: 0,
    totalRecommendations: 0,
    recentSessions: 0
  });
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ role: 'clinician' });

  useEffect(() => {
    loadAnalyticsData();
  }, [filters]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [analyticsData, statsData, trendsData] = await Promise.all([
        fetchUserAnalytics(filters),
        fetchAnalyticsStats(),
        fetchUsageTrends(30)
      ]);

      setAnalytics(analyticsData);
      setStats(statsData);
      setTrends(trendsData);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadAnalyticsData}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">User Analytics & Activity</h2>
            <p className="text-blue-100">Track user sign-ins, usage patterns, and platform engagement</p>
          </div>
          <Activity className="w-12 h-12 text-blue-200" />
        </div>
      </div>
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Users</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.totalActiveUsers}</p>
              <p className="text-sm text-gray-500 mt-1">With sign-in history</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Sessions</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.totalSessions}</p>
              <p className="text-sm text-gray-500 mt-1">{stats?.recentSessions} in last 24h</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <Monitor className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Usage Time</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.totalUsageHours}h</p>
              <p className="text-sm text-gray-500 mt-1">Avg {formatHours(stats?.avgUsagePerUser)} per user</p>
            </div>
            <div className="bg-purple-100 rounded-full p-3">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
      {/* Usage Trends Chart */}
      <UsageTrendsChart data={trends} />
      {/* User Analytics Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">User Activity Details</h3>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">Last 30 days</span>
            </div>
          </div>
        </div>
        <UserAnalyticsTable data={analytics} onRefresh={loadAnalyticsData} />
      </div>
    </div>
  );
}